import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Swords, User, Bell, LogOut, Shield, TrendingUp, Info, Edit, Trash2, Check, X, List, Building2 } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Logo from '@/components/Logo';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [invites, setInvites] = useState([]);
  const [matches, setMatches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [dashboardInfo, setDashboardInfo] = useState('');
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editedInfo, setEditedInfo] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [userRes, invitesRes, matchesRes, notifsRes, infoRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/invites/pending`, { withCredentials: true }),
        axios.get(`${API}/matches/my-matches`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications`, { withCredentials: true }),
        axios.get(`${API}/admin/dashboard-info`)
      ]);
      
      setUser(userRes.data);
      setInvites(invitesRes.data.filter(inv => inv.status === 'pending'));
      setMatches(matchesRes.data.slice(0, 5));
      setNotifications(notifsRes.data.filter(n => !n.read).slice(0, 5));
      setDashboardInfo(infoRes.data.content || '');
      setEditedInfo(infoRes.data.content || '');
      
      // Load deletion requests for admins
      if (userRes.data.role === 'admin') {
        try {
          const delRes = await axios.get(`${API}/admin/deletion-requests`, { withCredentials: true });
          setDeletionRequests(delRes.data);
        } catch (e) {
          // Ignore if endpoint fails
        }
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveInfo = async () => {
    try {
      await axios.put(`${API}/admin/dashboard-info`, { content: editedInfo }, { withCredentials: true });
      setDashboardInfo(editedInfo);
      setIsEditingInfo(false);
      toast.success('Dashboard info updated!');
    } catch (error) {
      toast.error('Failed to update info');
    }
  };

  const handleApproveDeletion = async (userId) => {
    try {
      await axios.delete(`${API}/admin/deletion-requests/${userId}/approve`, { withCredentials: true });
      toast.success('User deleted and anonymized');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve deletion');
    }
  };

  const handleRejectDeletion = async (userId) => {
    try {
      await axios.delete(`${API}/admin/deletion-requests/${userId}/reject`, { withCredentials: true });
      toast.success('Deletion request rejected');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to reject deletion');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await axios.put(`${API}/invites/${inviteId}/accept`, {}, { withCredentials: true });
      toast.success('Invite accepted! Match created');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await axios.put(`${API}/invites/${inviteId}/decline`, {}, { withCredentials: true });
      toast.success('Invite declined');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to decline invite');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">{t('loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="bg-[#111111] border-b border-[#27272A] sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Logo size="xl" />
            <h1 className="text-xl sm:text-3xl font-semibold tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>Ranking</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSwitcher />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/profile')}
              className="text-white hover:bg-white/10 h-8 w-8 sm:h-10 sm:w-10"
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-white/10 relative h-8 w-8 sm:h-10 sm:w-10"
                  data-testid="notification-bell"
                >
                  <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                  {(notifications.length > 0 || (user?.role === 'admin' && deletionRequests.length > 0)) && (
                    <span className="absolute top-0 right-0 w-2 h-2 bg-[#FF3B30] rounded-full"></span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 sm:w-80 bg-[#111111] border-[#27272A] p-0" align="end">
                <div className="p-3 border-b border-[#27272A]">
                  <h4 className="font-semibold text-white">{t('notifications')}</h4>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {/* Admin Deletion Requests in Bell */}
                  {user?.role === 'admin' && deletionRequests.length > 0 && (
                    <div className="border-b border-[#27272A]">
                      <p className="text-xs text-yellow-500 font-semibold px-3 py-2 bg-yellow-500/10">
                        {t('pendingDeletionRequests')} ({deletionRequests.length})
                      </p>
                      {deletionRequests.map(req => (
                        <div key={req.user_id} className="p-3 border-b border-[#27272A] hover:bg-white/5" data-testid="deletion-request-item">
                          <p className="text-sm text-white font-semibold">{req.name}</p>
                          <p className="text-xs text-gray-400">{req.email}</p>
                          <p className="text-xs text-gray-500 mt-1">{t('reason')}: {req.deletion_reason || t('notProvided')}</p>
                          <div className="flex gap-2 mt-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleApproveDeletion(req.user_id)}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                              data-testid="approve-deletion-btn"
                            >
                              <Check className="w-3 h-3 mr-1" /> {t('approve')}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleRejectDeletion(req.user_id)} 
                              className="border-[#27272A] text-white text-xs h-7"
                              data-testid="reject-deletion-btn"
                            >
                              <X className="w-3 h-3 mr-1" /> {t('deny')}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Regular Notifications */}
                  {notifications.length === 0 && !(user?.role === 'admin' && deletionRequests.length > 0) ? (
                    <p className="text-gray-400 text-sm p-4 text-center">{t('noNewNotifications')}</p>
                  ) : (
                    notifications.map((notif, idx) => (
                      <div key={idx} className="p-3 border-b border-[#27272A] hover:bg-white/5">
                        <p className="text-sm text-white">{notif.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {(user?.role === 'admin' || user?.role === 'club_admin') && (
              <Button 
                onClick={() => navigate('/admin')}
                className="bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-full font-bold relative"
              >
                <Shield className="w-4 h-4 mr-2" />
                {t('admin')}
                {user?.role === 'admin' && deletionRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full text-xs flex items-center justify-center text-black font-bold">
                    {deletionRequests.length}
                  </span>
                )}
              </Button>
            )}
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-[#27272A] hover:bg-white/10 text-white rounded-full text-sm sm:text-base"
            >
              <LogOut className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8" data-testid="dashboard">
        {/* Admin: Deletion Requests Alert */}
        {user?.role === 'admin' && deletionRequests.length > 0 && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 mb-4 sm:mb-6">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-yellow-500 flex items-center gap-2 text-base sm:text-lg">
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                {t('pendingDeletionRequests')} ({deletionRequests.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-2">
                {deletionRequests.map(req => (
                  <div key={req.user_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm sm:text-base truncate">{req.name}</p>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">{req.email}</p>
                      <p className="text-xs text-gray-500">{t('reason')}: {req.deletion_reason || t('notProvided')}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8">
                            <Check className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111111] border-[#27272A] mx-4 max-w-md">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">{t('approveDeletion')}</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              {t('approveDeletionDesc')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-[#27272A] text-white">{t('cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleApproveDeletion(req.user_id)} className="bg-green-600">
                              {t('approve')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Button size="sm" variant="outline" onClick={() => handleRejectDeletion(req.user_id)} className="border-[#27272A] text-white">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Info */}
        {(dashboardInfo || user?.role === 'admin') && (
          <Card className="bg-[#111111] border-[#27272A] mb-6">
            <CardContent className="py-4">
              {isEditingInfo ? (
                <div className="space-y-3">
                  <textarea
                    value={editedInfo}
                    onChange={(e) => setEditedInfo(e.target.value)}
                    placeholder={t('enterInfoForUsers')}
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleSaveInfo} className="bg-[#007AFF] hover:bg-[#0066D6] text-white">
                      {t('save')}
                    </Button>
                    <Button variant="outline" onClick={() => { setIsEditingInfo(false); setEditedInfo(dashboardInfo); }} className="border-[#27272A] text-white">
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Info className="w-5 h-5 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    <p className="text-white whitespace-pre-wrap">{dashboardInfo || t('noAnnouncements')}</p>
                  </div>
                  {user?.role === 'admin' && (
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingInfo(true)} className="text-gray-400 hover:text-white">
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Welcome Section */}
        <div className="mb-4 sm:mb-8 animate-fade-in">
          <h2 className="text-xl sm:text-3xl font-semibold mb-1 sm:mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('welcomeBack', { name: user?.name })}
          </h2>
          <p className="text-sm sm:text-base text-gray-400">{t('trackMatches')}</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <Button 
            data-testid="my-rankings-btn"
            onClick={() => navigate('/my-rankings')}
            className="bg-gradient-to-br from-[#007AFF] to-[#0055CC] hover:from-[#0066D6] hover:to-[#0044AA] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold shadow-lg hover-lift btn-press animate-slide-up stagger-1"
          >
            <Trophy className="w-6 h-6 sm:w-7 sm:h-7" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('myRankings')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('yourEloScores')}</div>
            </div>
          </Button>
          <Button 
            data-testid="view-rankings-btn"
            onClick={() => navigate('/ranking-lists')}
            className="bg-[#111111] hover:bg-[#1A1A1A] border border-[#27272A] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold hover-lift btn-press animate-slide-up stagger-2"
          >
            <TrendingUp className="w-6 h-6 sm:w-7 sm:h-7" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('viewRankings')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('browseAllLists')}</div>
            </div>
          </Button>
          <Button 
            data-testid="club-leaderboard-btn"
            onClick={() => navigate('/club-leaderboard')}
            className="bg-[#111111] hover:bg-[#1A1A1A] border border-[#27272A] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold hover-lift btn-press animate-slide-up stagger-3"
          >
            <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-400" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('clubLeaderboard')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('clubRanking')}</div>
            </div>
          </Button>
          <Button 
            data-testid="all-matches-btn"
            onClick={() => navigate('/admin/all-matches')}
            className="bg-[#111111] hover:bg-[#1A1A1A] border border-[#27272A] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold hover-lift btn-press animate-slide-up stagger-4"
          >
            <List className="w-6 h-6 sm:w-7 sm:h-7" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('allMatches')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('viewAllMatches')}</div>
            </div>
          </Button>
          <Button 
            data-testid="send-invite-btn"
            onClick={() => navigate('/send-invite')}
            className="bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold hover-lift btn-press animate-slide-up stagger-5"
          >
            <Swords className="w-6 h-6 sm:w-7 sm:h-7" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('challengePlayer')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('sendInvite')}</div>
            </div>
          </Button>
          <Button 
            onClick={() => navigate('/profile')}
            className="bg-[#111111] hover:bg-[#1A1A1A] border border-[#27272A] text-white rounded-xl p-3 sm:p-4 h-auto flex flex-col items-center gap-2 font-bold hover-lift btn-press animate-slide-up stagger-6"
          >
            <User className="w-6 h-6 sm:w-7 sm:h-7" />
            <div className="text-center">
              <div className="text-xs sm:text-sm">{t('profile')}</div>
              <div className="text-[10px] sm:text-xs opacity-80 hidden sm:block">{t('updateInfo')}</div>
            </div>
          </Button>
        </div>

        {/* Pending Invites */}
        {invites.length > 0 && (
          <Card className="bg-[#111111] border-[#27272A] mb-4 sm:mb-8 card-hover animate-slide-up">
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                <Swords className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF3B30]" />
                {t('pendingInvites')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3 px-3 sm:px-6">
              {invites.map(invite => (
                <div 
                  key={invite.invite_id} 
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4"
                  data-testid="invite-card"
                >
                  <div className="min-w-0">
                    <p className="text-white font-bold text-sm sm:text-base truncate">{invite.sender_name}</p>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">{invite.list_name}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button 
                      data-testid="accept-invite-btn"
                      onClick={() => handleAcceptInvite(invite.invite_id)}
                      className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full text-xs sm:text-sm h-8 sm:h-9 px-3"
                    >
                      {t('accept')}
                    </Button>
                    <Button 
                      onClick={() => handleDeclineInvite(invite.invite_id)}
                      variant="outline"
                      className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                    >
                      {t('decline')}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent Matches */}
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white">{t('recentMatches')}</CardTitle>
          </CardHeader>
          <CardContent>
            {matches.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noMatchesYet')}</p>
            ) : (
              <div className="space-y-3">
                {matches.map(match => (
                  <div 
                    key={match.match_id} 
                    className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 hover:border-[#007AFF]/50 transition-all cursor-pointer"
                    onClick={() => navigate(`/matches/${match.match_id}`)}
                    data-testid="match-card"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-white font-bold">{match.player1_name}</p>
                          <p className="text-2xl font-black text-[#007AFF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {match.player1_score ?? '-'}
                          </p>
                        </div>
                        <div className="text-gray-500 font-bold">VS</div>
                        <div className="text-center">
                          <p className="text-white font-bold">{match.player2_name}</p>
                          <p className="text-2xl font-black text-[#FF3B30]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {match.player2_score ?? '-'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge 
                          className={`${
                            match.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                            match.status === 'pending_confirmation' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          } rounded-full`}
                        >
                          {match.status.replace('_', ' ')}
                        </Badge>
                        <p className="text-sm text-gray-400 mt-1">{match.list_name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default Dashboard;